import React, { useRef, useState } from 'react';
import { 
  Download, 
  Printer, 
  Copy, 
  Check, 
  ShieldCheck, 
  Lock, 
  FileText, 
  Building2, 
  UserCheck, 
  Scale, 
  DollarSign, 
  Plane, 
  Calendar,
  AlertCircle,
  PlusCircle,
  ExternalLink,
  Key,
  Shield,
  FileCheck,
  Scissors,
  RotateCcw,
  Eye,
  Loader2,
  Files
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { sanitizeClonedDocForPdf } from '../utils/pdfColorSanitizer';

interface ExecutiveAgreementHubProps {
  showToast?: (msg: string, type?: 'success' | 'info' | 'warning' | 'error') => void;
}

export const FOUNDER_AGREEMENT_RAW_TEXT = `AZURA SERAPH™ PTY LTD
Registration Number: 2024/560516/07

EXECUTIVE PARTNER BRIEF & FOUNDER AGREEMENT
CONFIDENTIAL & PROPRIETARY

DATE: September 20th, 2026

PARTIES:

1. Raksha Singh — Founder & Chief Executive Officer (CEO), Identity Number:

2. Thato Donald Makgari — Strategic Co-Founder & Chief Operating Officer (COO), Identity Number: 

PRIMARY ENTITY: Azura Seraph™ Pty Ltd (South Africa)
HOLDING STRUCTURE: Delaware C-Corporation (Post-Y Combinator Flip)


1. EXECUTIVE PURPOSE & SCOPE

This document formalizes the strategic partnership, equity allocation, executive compensation parameters, and operational framework between Raksha Singh (CEO) and Thato Donald Makgari (COO) for the building and global scaling of AzuraOS under Azura Seraph™ Pty Ltd and its parent holding structures.

2. EXECUTIVE DESIGNATIONS & LANES OF RESPONSIBILITY

2.1 Founder & Chief Executive Officer (CEO) — Raksha Singh

- Full-stack software architecture, product engineering, local-first trust engines, and AI integrations.

- Global investor relations, capital raising, and primary Y Combinator representation.

- Strategic brand positioning, enterprise architecture, and primary platform governance.

2.2 Co-Founder & Strategic Chief Operating Officer (COO) — Thato D. Makgari:

- Operational execution, institutional procurement, and enterprise pipeline management.

- Corporate Enterprise & Supplier Development (ESD) fund partnerships, banking cohort acquisition, and government compliance contracts.

- South African subsidiary operational leadership, margin governance, and supply chain intelligence frameworks.

3. EQUITY ARCHITECTURE & VESTING MECHANICS

3.1 Equity Split Allocation:
- Raksha Singh (Founder & CEO): 70% Equity Stake

  (Reflecting total historical IP creation, software development, infrastructure deployment, Google Cloud Partner status, and initial platform execution).

- Thato (Co-Founder & COO): 30% Equity Stake

  (Reflecting operational leadership, institutional procurement, enterprise ESD scaling, and long-term executive execution).

3.2 Vesting Schedule (Standard Tier-1 Silicon Valley Governance):

- Total Vesting Horizon: 48 Months (4 Years).

- One-Year Cliff: 12-Month Cliff. Zero percent (0%) equity vests prior to Month 12. On exact Month 12, twenty-five percent (25%) of respective allocations vest automatically.

- Monthly Vesting: The remaining seventy-five percent (75%) vests in equal monthly increments (1/36th per month) across Months 13 through 48.

4. PRE-SEED STIPEND & Y COMBINATOR BATCH MECHANICS (SAN FRANCISCO)

During the 3-month Y Combinator (YC) residency phase in San Francisco, California USA, the company operates under strict capital preservation parameters to maximize corporate runway:

4.1 De-Riskeing Founder Stipend:

- Direct Monthly Stipend: $5,000 USD / month (~ +-R81,000 ZAR - forex conversion rates dependant) paid directly to personal bank accounts.

- Purpose: Fully covers all domestic living expenses, mortgages, school fees, and financial obligations back home in South Africa with zero personal financial risk.

4.2 Corporate-Funded Operational Logistics (100% Company Paid):

- Accommodation: Shared executive 2-bedroom corporate housing / Airbnb in the Bay Area.

- Transit: Economy return international airfare (South Africa <-> San Francisco), USA visa processing fees, and local Bay Area transit pool.

- Healthcare & Insurance: Primary international travel and health insurance coverage.

- Shared Operating Pool: Monthly team allocation for groceries, work meals, and YC cohort events.

- Emergency Reserve: $2,000 USD company reserve fund for emergency contingencies.

Non-Immigrant Business Entry Requirements (B1/B2 Visa)

Vaccination Status:

COVID-19: The mandate requiring international non-immigrant air travelers to show proof of COVID-19 vaccination was officially lifted on May 11, 2023. You do not need to show proof of COVID-19 vaccination or testing.

RoutineVaccines: No proof of routine childhood or adult vaccines (MMR, Tetanus, Yellow Fever, etc.) is checked at border entry for B1/B2 business visitors unless traveling directly from a region experiencing a specific international health emergency outbreak.

Core Entry Requirements at U.S. Customs and Border Protection (CBP):

Valid Passport: Must be valid for at least 6 months beyond your intended period of stay in the US.

Valid B1/B2 Visa (or ESTA): Issued and stamped in your passport.

Demonstrated Intent of Return: CBP officers look for non-immigrant intent—meaning you must be able to prove strong ties to your home country (e.g., employment, registered business entity, family, property) - *this is also compulsory during Visa Interview at the USA Consulate / Embassy. 

Proof of Financial Support: Ability to cover expenses while in the US without working illegally for a domestic US employer.

Clear Business Purpose: Being able to state clearly that you are attending meetings, an accelerator program (like Y Combinator), investor pitches, or trade conferences, which are fully permitted under B1 business visitor classification. 

5. POST-SEED EXECUTIVE COMPENSATION ESCALATOR

Upon the successful conclusion of the 3 Months - YC residency (Demo Day) and the closing of the institutional Seed Capital Round ($1.5M - $3.0M USD):

- Founder Stipends convert into full market-rate executive compensation packages (scaling up to $10,000 USD / month).

- 100% Full-time dedicated executive focus is formalized across US and South African operating entities.


IN WITNESS WHEREOF, the parties hereto have executed this Executive Partner Agreement as of the date first written above.


________________________________________
Raksha Singh - Identity Number:
_____________________________
Founder & Chief Executive Officer
Azura Seraph™ Pty Ltd


________________________________________
Thato Donald Makgari - Identity Number: ___________________________
Co-Founder & Chief Operating Officer
Azura Seraph™ Pty Ltd`;

export const STRATEGIC_ADDENDUM_RAW_TEXT = `AZURA SERAPH™ PTY LTD
2024/560516/07

STRATEGIC ADDENDUM: CORPORATE STRUCTURE, GOVERNANCE & LOGISTICS

CONFIDENTIAL & ATTACHMENT TO FOUNDER AGREEMENT

DATE: September 20th, 2026

1. THE DELAWARE C-CORP "FLIP" & SUBSIDIARY MODEL

As a condition of Y Combinator acceptance and institutional Venture Capital investment, Azura Seraph™ Pty Ltd, will undergo a standard corporate restructuring:

1.1 Parent Entity: A Delaware C-Corporation will be incorporated in the United States as the top-level parent holding company. All venture investment capital, international IP, and global equity issued will sit within the Delaware C-Corp.

1.2 African Operating Subsidiary: Azura Seraph™ Pty Ltd (South Africa) will become a 100% wholly-owned subsidiary of the Delaware parent corporation.

1.3 Leadership Relocation Flexibility:

- The COO is NOT required to permanently relocate his family to the United States.

- Following the 3-month YC residency in San Francisco, the COO maintains total operational flexibility to reside in South Africa and lead African Enterprise/ESD operations as HEAD of the local operating subsidiary.

- CEO maintains global market expansion, US entity presence, and investor relations.

2. CAP TABLE GOVERNANCE & "WHY 70/30 EQUITY"

2.1 Valuation & Value Creation:

A 30% co-founder equity stake in a Delaware C-Corporation represents an exceptionally high, institutional-grade equity allocation. Upon hitting growth and Series-A milestones, a 30% equity position represents significant generational wealth.

2.2 Asymmetric Pre-Seed Risk Recognition:

The 70/30 ratio reflects:

- Prior IP Creation: 100% of product architecture, codebase, live deployment (azuraos.app), Google Cloud infrastructure, and initial capital risk were executed solely by the CEO prior to partnership entry.

- Cap Table Integrity: Maintaining CEO controlling equity (70%) preserves clean decision-making authority required by tier-1 VCs during institutional financing rounds.

3. Y COMBINATOR APPLICATION FILING ALIGNMENT

3.1 Formal Application Status: 

Both parties enter the Y Combinator application as unified, equal partners in execution with shared military discipline foundations, 23 years of mutual trust, and complementary technical/operational capability.

3.2 Academic / Post-Grad Program Contingency: 

Post-grad MBA applications or potential schedule overlaps will be managed internally following formal accelerator acceptance milestones, requesting virtual flexibility or deferral windows where appropriate, without compromising YC application submission integrity.

4. ACKNOWLEDGMENT OF TERMS

This Addendum serves as an integral annexure to the primary Executive Partner Agreement executed between Raksha Singh - Identity Number: _______________________________
and Thato Donald Makgari - Identity Number: ___________________________


________________________________________
Raksha Singh
Founder & CEO | Azura Seraph™ Pty Ltd


________________________________________
Thato Donald Makgari
Co-Founder & COO | Azura Seraph™ Pty Ltd`;

export function ExecutiveAgreementHub({ showToast }: ExecutiveAgreementHubProps) {
  const [activeDoc, setActiveDoc] = useState<'agreement' | 'addendum'>('agreement');
  const [copied, setCopied] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  
  // Identity numbers and addendum text states with localStorage synchronization
  const [rakshaIdNumber, setRakshaIdNumberState] = useState(() => {
    try {
      return localStorage.getItem('azuraos_raksha_id_number') || '';
    } catch {
      return '';
    }
  });
  const [thatoIdNumber, setThatoIdNumberState] = useState(() => {
    try {
      return localStorage.getItem('azuraos_thato_id_number') || '';
    } catch {
      return '';
    }
  });

  const setRakshaIdNumber = (val: string) => {
    setRakshaIdNumberState(val);
    try {
      localStorage.setItem('azuraos_raksha_id_number', val);
    } catch {}
  };

  const setThatoIdNumber = (val: string) => {
    setThatoIdNumberState(val);
    try {
      localStorage.setItem('azuraos_thato_id_number', val);
    } catch {}
  };

  const [generatingDoc, setGeneratingDoc] = useState<'agreement' | 'addendum' | 'bundle' | null>(null);

  const [addendumText, setAddendumText] = useState(() => {
    return localStorage.getItem('azuraos_executive_addendum_draft') || STRATEGIC_ADDENDUM_RAW_TEXT;
  });
  const [showFormalSignatures, setShowFormalSignatures] = useState(true);
  const [addendumViewMode, setAddendumViewMode] = useState<'formatted' | 'verbatim'>('formatted');
  const [showAddendumEditor, setShowAddendumEditor] = useState(true);

  const handleRemoveDuplicateSignature = () => {
    // Matches trailing signature block (underscores, names, titles)
    const signaturePattern = /\n+\s*_{5,}[\s\S]*?(Raksha|Thato)[\s\S]*$/i;
    let cleaned = addendumText;
    if (signaturePattern.test(addendumText)) {
      cleaned = addendumText.replace(signaturePattern, '').trim();
    } else {
      const lines = addendumText.split('\n');
      let cutoff = lines.length;
      for (let i = lines.length - 1; i >= 0; i--) {
        const l = lines[i].trim();
        if (!l || /^_{5,}/.test(l) || /^(Raksha Singh|Thato Donald Makgari|Founder & CEO|Co-Founder & COO)/i.test(l)) {
          cutoff = i;
        } else {
          break;
        }
      }
      cleaned = lines.slice(0, cutoff).join('\n').trim();
    }
    handleSaveAddendum(cleaned);
    if (showToast) showToast('✂️ Duplicate signature lines stripped from text editor!', 'success');
  };

  // Dynamic Addendum Parser: Parses raw text in real time into styled legal blocks
  const parseAddendum = (rawText: string, stripSignaturesFromBody: boolean) => {
    let content = rawText.trim();
    const hasDetectedSignatures = /_{5,}[\s\S]*?(Raksha|Thato|Founder|COO)/i.test(content);

    if (stripSignaturesFromBody) {
      content = content.replace(/\n+\s*_{5,}[\s\S]*?(Raksha|Thato)[\s\S]*$/i, '').trim();
    }

    const rawLines = content.split('\n');
    const headerLines: string[] = [];
    const bodyLines: string[] = [];
    let foundFirstSection = false;

    for (const line of rawLines) {
      const trimmed = line.trim();
      if (!foundFirstSection && /^[1-9]\.\s+[A-Z]/.test(trimmed)) {
        foundFirstSection = true;
      }
      if (!foundFirstSection) {
        if (trimmed) headerLines.push(trimmed);
      } else {
        bodyLines.push(line);
      }
    }

    let headerCompany = 'AZURA SERAPH™ (PTY) LTD';
    let headerReg = '(Registration Number: 2024/560516/07) • Republic of South Africa';
    let headerTitle = 'STRATEGIC ADDENDUM: CORPORATE STRUCTURE, GOVERNANCE & LOGISTICS';
    let headerMeta = 'CONFIDENTIAL & ATTACHMENT TO FOUNDER AGREEMENT';
    let headerDate = 'DATE OF EFFECT: September 20th, 2026';

    for (const hl of headerLines) {
      if (/AZURA SERAPH/i.test(hl)) headerCompany = hl;
      else if (/2024\/|Registration/i.test(hl)) {
        headerReg = hl.includes('Registration') ? hl : `(Registration Number: ${hl}) • Republic of South Africa`;
      } else if (/STRATEGIC ADDENDUM/i.test(hl)) headerTitle = hl;
      else if (/CONFIDENTIAL/i.test(hl)) headerMeta = hl;
      else if (/DATE/i.test(hl)) headerDate = hl.startsWith('DATE') ? hl : `DATE: ${hl}`;
    }

    interface SectionItem {
      type: 'bullet' | 'sub' | 'p';
      code?: string;
      label?: string;
      text: string;
    }

    interface ParsedSection {
      title: string;
      items: SectionItem[];
    }

    const sections: ParsedSection[] = [];
    let curSec: ParsedSection | null = null;

    for (const rawLine of bodyLines) {
      const trimmed = rawLine.trim();
      if (!trimmed) continue;

      const secMatch = trimmed.match(/^([0-9]+)\.\s+(.*)/);
      if (secMatch) {
        if (curSec) sections.push(curSec);
        curSec = {
          title: trimmed,
          items: []
        };
        continue;
      }

      if (!curSec) {
        curSec = {
          title: 'GENERAL PROVISIONS',
          items: []
        };
      }

      const bulletMatch = trimmed.match(/^[-•*]\s*(.*)/);
      if (bulletMatch) {
        curSec.items.push({
          type: 'bullet',
          text: bulletMatch[1]
        });
        continue;
      }

      const subMatch = trimmed.match(/^([0-9]+\.[0-9]+)\s*(.*?)(:\s*|$)(.*)/);
      if (subMatch) {
        curSec.items.push({
          type: 'sub',
          code: subMatch[1],
          label: subMatch[2] ? `${subMatch[2]}:` : '',
          text: subMatch[4] || ''
        });
        continue;
      }

      curSec.items.push({
        type: 'p',
        text: trimmed
      });
    }

    if (curSec) sections.push(curSec);

    return {
      headerCompany,
      headerReg,
      headerTitle,
      headerMeta,
      headerDate,
      sections,
      hasDetectedSignatures
    };
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(activeDoc === 'agreement' ? FOUNDER_AGREEMENT_RAW_TEXT : (addendumText || STRATEGIC_ADDENDUM_RAW_TEXT));
    setCopied(true);
    if (showToast) showToast('📋 Document copy copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2500);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async (targetDoc: 'agreement' | 'addendum' | 'bundle' = activeDoc) => {
    setGeneratingDoc(targetDoc);
    setIsGeneratingPdf(true);
    try {
      if (targetDoc === 'bundle') {
        // Master Governance Bundle: Compiles all 4 pages (Pages 1, 2, 3 of Agreement + Page 4 of Addendum)
        if (activeDoc !== 'agreement') {
          setActiveDoc('agreement');
          await new Promise((r) => setTimeout(r, 200));
        }

        const agreementCanvas = document.getElementById('founder-agreement-pdf-canvas');
        if (!agreementCanvas) throw new Error('Founder agreement canvas not found');
        const agreementSheets = agreementCanvas.querySelectorAll<HTMLElement>('.agreement-sheet');

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true
        });

        const pdfWidth = 210;
        const pdfHeight = 297;

        // Render 3 sheets of Founder Agreement
        for (let i = 0; i < agreementSheets.length; i++) {
          if (i > 0) pdf.addPage('a4', 'portrait');
          const sheet = agreementSheets[i];
          const canvas = await html2canvas(sheet, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
            windowWidth: 1024,
            onclone: (clonedDoc) => {
              sanitizeClonedDocForPdf(clonedDoc);
              const clonedSheets = clonedDoc.querySelectorAll<HTMLElement>('.agreement-sheet');
              clonedSheets.forEach((cs) => {
                cs.style.width = '794px';
                cs.style.minWidth = '794px';
                cs.style.maxWidth = '794px';
                cs.style.height = '1123px';
                cs.style.minHeight = '1123px';
                cs.style.maxHeight = '1123px';
                cs.style.boxSizing = 'border-box';
                cs.style.padding = '44px 50px';
                cs.style.margin = '0 auto';
                cs.style.backgroundColor = '#ffffff';
                cs.style.color = '#000000';
                cs.style.fontFamily = 'Charter, Cambria, "Times New Roman", Times, Georgia, serif';
                cs.style.boxShadow = 'none';
                cs.style.border = 'none';
                cs.style.borderRadius = '0';
              });
            }
          });
          const imgData = canvas.toDataURL('image/png');
          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        }

        // Switch to Addendum to render Page 4
        setActiveDoc('addendum');
        await new Promise((r) => setTimeout(r, 200));

        const addendumCanvas = document.getElementById('addendum-pdf-canvas');
        if (!addendumCanvas) throw new Error('Addendum canvas not found');
        const addendumSheet = addendumCanvas.querySelector<HTMLElement>('.agreement-sheet') || addendumCanvas;

        pdf.addPage('a4', 'portrait');
        const addendumCanvasImg = await html2canvas(addendumSheet, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: 1024,
          onclone: (clonedDoc) => {
            sanitizeClonedDocForPdf(clonedDoc);
            const clonedSheets = clonedDoc.querySelectorAll<HTMLElement>('.agreement-sheet');
            clonedSheets.forEach((cs) => {
              cs.style.width = '794px';
              cs.style.minWidth = '794px';
              cs.style.maxWidth = '794px';
              cs.style.height = '1123px';
              cs.style.minHeight = '1123px';
              cs.style.maxHeight = '1123px';
              cs.style.boxSizing = 'border-box';
              cs.style.padding = '38px 46px 36px 46px';
              cs.style.margin = '0 auto';
              cs.style.backgroundColor = '#ffffff';
              cs.style.color = '#000000';
              cs.style.fontFamily = 'Charter, Cambria, "Times New Roman", Times, Georgia, serif';
              cs.style.boxShadow = 'none';
              cs.style.border = 'none';
              cs.style.borderRadius = '0';
            });
          }
        });
        const addendumImgData = addendumCanvasImg.toDataURL('image/png');
        pdf.addImage(addendumImgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');

        pdf.save('Azura_Seraph_Executive_Governance_Master_Agreement_and_Addendum_2026.pdf');
        if (showToast) showToast('✅ Full 4-Page Executive Governance Master PDF downloaded successfully!', 'success');
        return;
      }

      // Single Document PDF Export (Target: 'agreement' or 'addendum')
      const target = targetDoc;
      const exportElementId = target === 'agreement' ? 'founder-agreement-pdf-canvas' : 'addendum-pdf-canvas';

      // If requested doc is not currently mounted, switch and wait for render
      if (activeDoc !== target) {
        setActiveDoc(target);
        await new Promise((r) => setTimeout(r, 180));
      }

      const exportElement = document.getElementById(exportElementId);
      if (!exportElement) {
        throw new Error(`Export canvas element #${exportElementId} not found`);
      }

      // Query all distinct A4 agreement sheets
      const sheetElements = exportElement.querySelectorAll<HTMLElement>('.agreement-sheet');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pdfWidth = 210;
      const pdfHeight = 297;

      const sheetsToRender = sheetElements.length > 0 ? Array.from(sheetElements) : [exportElement];

      for (let i = 0; i < sheetsToRender.length; i++) {
        const sheet = sheetsToRender[i];
        if (i > 0) {
          pdf.addPage('a4', 'portrait');
        }

        const canvas = await html2canvas(sheet, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: 1024,
          onclone: (clonedDoc) => {
            sanitizeClonedDocForPdf(clonedDoc);
            const clonedSheets = clonedDoc.querySelectorAll<HTMLElement>('.agreement-sheet');
            clonedSheets.forEach((cs) => {
              cs.style.width = '794px';
              cs.style.minWidth = '794px';
              cs.style.maxWidth = '794px';
              cs.style.height = '1123px';
              cs.style.minHeight = '1123px';
              cs.style.maxHeight = '1123px';
              cs.style.boxSizing = 'border-box';
              cs.style.padding = target === 'agreement' ? '44px 50px' : '38px 46px 36px 46px';
              cs.style.margin = '0 auto';
              cs.style.backgroundColor = '#ffffff';
              cs.style.color = '#000000';
              cs.style.fontFamily = 'Charter, Cambria, "Times New Roman", Times, Georgia, serif';
              cs.style.boxShadow = 'none';
              cs.style.border = 'none';
              cs.style.borderRadius = '0';
              cs.style.display = 'flex';
              cs.style.flexDirection = 'column';
              cs.style.justifyContent = 'space-between';
            });
          }
        });

        // Lossless PNG output for razor-sharp legal typography without JPEG artifacts
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      }

      const fileName = target === 'agreement'
        ? 'Azura_Seraph_Executive_Founder_Agreement_Singh_Makgari_2026.pdf'
        : 'Azura_Seraph_Strategic_Addendum_Corporate_Governance_2026.pdf';

      pdf.save(fileName);
      if (showToast) {
        showToast(
          target === 'agreement'
            ? '✅ Executive Partner Brief (3-Page A4) PDF downloaded successfully!'
            : '✅ Strategic Addendum (A4 Zero-Cut) PDF downloaded successfully!',
          'success'
        );
      }
    } catch (err) {
      console.error('PDF export failed:', err);
      if (showToast) showToast('⚠️ PDF export encountered an issue, opening print dialog instead.', 'info');
      window.print();
    } finally {
      setIsGeneratingPdf(false);
      setGeneratingDoc(null);
    }
  };

  const handleSaveAddendum = (val: string) => {
    setAddendumText(val);
    localStorage.setItem('azuraos_executive_addendum_draft', val);
  };

  return (
    <div className="space-y-6">
      {/* 1. TOP CONFIDENTIAL VAULT BANNER */}
      <div className="p-6 bg-[#101314] border border-[#242A2D] rounded-2xl shadow-lg flex flex-col lg:flex-row lg:items-center justify-between gap-4 no-print">
        <div className="flex items-start gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-[#181D1F] border border-[#2F363A] text-amber-400 flex items-center justify-center shrink-0">
            <Scale size={24} />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-wider font-extrabold px-2.5 py-0.5 rounded-full bg-amber-400/10 text-amber-300 border border-amber-400/30">
                {activeDoc === 'agreement' ? 'SUPER ADMIN VAULT • STRICTLY CONFIDENTIAL' : 'STRATEGIC GOVERNANCE ANNEXURE • DELAWARE C-CORP FLIP'}
              </span>
              <span className="text-[10px] font-mono text-gray-400">
                Entity Reg: 2024/560516/07
              </span>
              <span className="px-2 py-0.5 bg-sky-500/15 border border-sky-500/30 rounded text-[10px] font-bold text-sky-400 uppercase tracking-wider font-mono">
                Zero Page-Cut A4 Engine
              </span>
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">
              {activeDoc === 'agreement' 
                ? 'Executive Partner Brief & Founder Agreement' 
                : 'Strategic Addendum: Corporate Structure & Governance'}
            </h2>
            <p className="text-xs text-gray-300 max-w-2xl leading-relaxed">
              {activeDoc === 'agreement'
                ? 'Legally binding founder equity architecture, lanes of responsibility, YC batch stipends ($5,000/mo), and Delaware C-Corp post-seed mechanics for Raksha Singh (CEO) & Thato D. Makgari (COO).'
                : 'Delaware C-Corp top-level parent entity, African operating subsidiary, YC San Francisco residency terms, and bilateral executive governance addendum for Raksha Singh & Thato Donald Makgari.'}
            </p>
          </div>
        </div>

        {/* Action Toolbar */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            onClick={handleCopyText}
            className="px-3 py-2 bg-[#1C2224] hover:bg-[#252C2F] text-gray-200 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer shadow-md"
            title="Copy Raw Document Copy"
          >
            {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-amber-400" />}
            <span>{copied ? 'Copied!' : 'Copy Text'}</span>
          </button>

          <button
            onClick={handlePrint}
            className="px-3 py-2 bg-[#1C2224] hover:bg-[#252C2F] text-gray-200 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer shadow-md"
            title="Print or Save via Browser (Zero Page Cuts)"
          >
            <Printer size={14} className="text-sky-400" />
            <span>Print View</span>
          </button>

          {/* DEDICATED STRATEGIC ADDENDUM PDF DOWNLOAD BUTTON */}
          <button
            onClick={() => handleDownloadPdf('addendum')}
            disabled={isGeneratingPdf}
            className={`px-3.5 py-2 text-xs font-extrabold rounded-xl shadow-lg transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50 ${
              activeDoc === 'addendum'
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black ring-2 ring-amber-400/40'
                : 'bg-[#1C2224] hover:bg-[#252C2F] text-amber-300 hover:text-white border border-amber-500/40'
            }`}
            title="Download Strategic Addendum PDF (A4 Single-Page Zero Cut)"
          >
            {generatingDoc === 'addendum' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            <span>{generatingDoc === 'addendum' ? 'Rendering Addendum...' : 'Download Addendum PDF'}</span>
          </button>

          {/* DEDICATED FOUNDER AGREEMENT PDF DOWNLOAD BUTTON */}
          <button
            onClick={() => handleDownloadPdf('agreement')}
            disabled={isGeneratingPdf}
            className={`px-3.5 py-2 text-xs font-extrabold rounded-xl shadow-lg transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50 ${
              activeDoc === 'agreement'
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black ring-2 ring-amber-400/40'
                : 'bg-[#1C2224] hover:bg-[#252C2F] text-amber-300 hover:text-white border border-amber-500/40'
            }`}
            title="Download Executive Partner Brief PDF (3-Page Zero Cut)"
          >
            {generatingDoc === 'agreement' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            <span>{generatingDoc === 'agreement' ? 'Rendering Brief...' : 'Download Executive Brief PDF'}</span>
          </button>

          {/* FULL 4-PAGE GOVERNANCE MASTER BUNDLE */}
          <button
            onClick={() => handleDownloadPdf('bundle')}
            disabled={isGeneratingPdf}
            className="px-3 py-2 bg-gradient-to-r from-sky-500/20 to-teal-500/20 hover:from-sky-500/30 hover:to-teal-500/30 text-sky-200 hover:text-white text-xs font-bold rounded-xl border border-sky-500/40 transition flex items-center gap-1.5 cursor-pointer shadow-md disabled:opacity-50"
            title="Download Complete 4-Page Governance Master PDF (Founder Agreement + Strategic Addendum Combined)"
          >
            {generatingDoc === 'bundle' ? <Loader2 size={14} className="animate-spin text-sky-400" /> : <Files size={14} className="text-sky-400" />}
            <span className="hidden sm:inline">{generatingDoc === 'bundle' ? 'Compiling 4-Page Master...' : 'Download 4-Page Master PDF'}</span>
            <span className="sm:hidden">{generatingDoc === 'bundle' ? 'Compiling...' : 'Master PDF'}</span>
          </button>
        </div>
      </div>

      {/* 2. DOCUMENT SELECTOR TABS */}
      <div className="flex flex-wrap items-center gap-2 p-1.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl w-fit no-print">
        <button
          onClick={() => setActiveDoc('agreement')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
            activeDoc === 'agreement'
              ? 'bg-amber-500 text-black shadow-md font-extrabold'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FileText size={15} />
          <span>1. Executive Partner &amp; Founder Agreement (3-Page Formal)</span>
          <span className={`px-1.5 py-0.2 text-[9px] rounded font-mono ${activeDoc === 'agreement' ? 'bg-black/20 text-black font-extrabold' : 'bg-white/10 text-gray-400'}`}>PDF READY</span>
        </button>

        <button
          onClick={() => setActiveDoc('addendum')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
            activeDoc === 'addendum'
              ? 'bg-amber-500 text-black shadow-md font-extrabold'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <PlusCircle size={15} />
          <span>2. Strategic Addendum: Corporate Structure &amp; Governance (1-Page A4)</span>
          <span className={`px-1.5 py-0.2 text-[9px] rounded font-mono ${activeDoc === 'addendum' ? 'bg-black/20 text-black font-extrabold' : 'bg-white/10 text-gray-400'}`}>PDF READY</span>
        </button>
      </div>

      {/* 3. DOCUMENT DISPLAY - TAB 1: FOUNDER AGREEMENT */}
      {activeDoc === 'agreement' && (
        <div className="space-y-4">
          {/* Quick ID Number Assistant (Optional Helper) */}
          <div className="p-4 bg-[#111416] border border-[#22282B] rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs no-print">
            <div className="flex items-center gap-2 text-gray-300">
              <Key size={16} className="text-amber-400" />
              <span>Identity Number Insertion:</span>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <input
                type="text"
                placeholder="Raksha Singh ID Number"
                value={rakshaIdNumber}
                onChange={(e) => setRakshaIdNumber(e.target.value)}
                className="bg-[#181D1F] border border-[#2A2F31] rounded-xl px-3 py-1.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-400 text-xs"
              />
              <input
                type="text"
                placeholder="Thato D. Makgari ID Number"
                value={thatoIdNumber}
                onChange={(e) => setThatoIdNumber(e.target.value)}
                className="bg-[#181D1F] border border-[#2A2F31] rounded-xl px-3 py-1.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-400 text-xs"
              />
            </div>
          </div>

          {/* THE DOCUMENT CONTAINER (Interactive UI & PDF Canvas) */}
          <div id="printable-agreement-root" className="bg-[#121516] border border-[#242A2D] rounded-2xl p-4 sm:p-8 shadow-lg overflow-x-auto">
            <div id="founder-agreement-pdf-canvas" className="space-y-8">
              
              {/* ========================================================================= */}
              {/* SHEET 1 OF 3: PREAMBLE, PARTIES, PURPOSE & LANES OF RESPONSIBILITY       */}
              {/* ========================================================================= */}
              <div>
                <div className="flex items-center justify-between px-2 pb-2 text-xs font-mono text-gray-400 no-print">
                  <span className="flex items-center gap-1.5 text-amber-400 font-bold">
                    <FileText size={14} />
                    PAGE 1 OF 3 • EXECUTIVE PURPOSE &amp; RESPONSIBILITIES
                  </span>
                  <span className="text-[11px] text-gray-500">A4 Standard Format (794×1123px)</span>
                </div>

                <div 
                  className="agreement-sheet bg-white text-slate-900 mx-auto shadow-md border border-gray-300 p-8 sm:p-12 flex flex-col justify-between"
                  style={{ 
                    fontFamily: 'Charter, Cambria, "Times New Roman", Times, Georgia, serif',
                    width: '100%',
                    maxWidth: '794px',
                    minHeight: '1123px',
                    boxSizing: 'border-box'
                  }}
                >
                  <div className="space-y-5">
                    {/* FORMAL LEGAL HEADER */}
                    <div className="text-center pb-4 border-b-2 border-black space-y-1">
                      <div className="text-[10px] tracking-[0.25em] text-gray-600 uppercase font-sans font-bold">
                        CONFIDENTIAL &amp; PROPRIETARY • EXECUTIVE GOVERNANCE
                      </div>
                      <h1 className="text-2xl font-bold tracking-tight text-black uppercase pt-1">
                        AZURA SERAPH™ (PTY) LTD
                      </h1>
                      <div className="text-xs text-gray-700">
                        (Registration Number: 2024/560516/07) • Republic of South Africa
                      </div>
                      <div className="pt-2">
                        <span className="text-xs font-bold tracking-wider text-black uppercase border-y border-black py-0.5 px-3 inline-block">
                          EXECUTIVE PARTNER BRIEF &amp; FOUNDER GOVERNANCE AGREEMENT
                        </span>
                      </div>
                    </div>

                    {/* DATE & PARTIES TO AGREEMENT */}
                    <div className="border border-gray-300 bg-[#FAFAFA] p-3.5 space-y-2 text-xs leading-relaxed">
                      <div className="flex items-center justify-between font-bold text-gray-900 border-b border-gray-200 pb-1 text-[11px]">
                        <span>DATE OF EFFECT: September 20th, 2026</span>
                        <span className="text-gray-500 font-normal">GOVERNING LAW: South Africa / Delaware US</span>
                      </div>

                      <div className="space-y-1.5 pt-1">
                        <div className="font-bold text-gray-950 uppercase tracking-wide text-[11px]">
                          ENTERED INTO BY AND BETWEEN:
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-900">
                          <div className="border-l-2 border-black pl-2.5">
                            <strong>1. RAKSHA SINGH</strong><br />
                            Founder &amp; Chief Executive Officer (CEO)<br />
                            Identity Number: <span className="font-mono font-semibold underline">{rakshaIdNumber || '_____________________________'}</span>
                          </div>
                          <div className="border-l-2 border-black pl-2.5">
                            <strong>2. THATO DONALD MAKGARI</strong><br />
                            Strategic Co-Founder &amp; Chief Operating Officer (COO)<br />
                            Identity Number: <span className="font-mono font-semibold underline">{thatoIdNumber || '___________________________'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between text-[11px] text-gray-700 gap-1">
                        <div><strong>PRIMARY ENTITY:</strong> Azura Seraph™ (Pty) Ltd (South Africa)</div>
                        <div><strong>HOLDING STRUCTURE:</strong> Delaware C-Corporation (Post-Y Combinator Flip)</div>
                      </div>
                    </div>

                    {/* SECTION 1 */}
                    <div className="space-y-1.5">
                      <h2 className="text-xs font-bold uppercase tracking-wider text-black border-b border-gray-300 pb-1">
                        1. EXECUTIVE PURPOSE &amp; SCOPE
                      </h2>
                      <p className="text-gray-900 text-xs leading-relaxed text-justify">
                        This document formalizes the strategic partnership, equity allocation, executive compensation parameters, and operational governance framework between Raksha Singh (CEO) and Thato Donald Makgari (COO) for the founding, technical development, and global scaling of AzuraOS under Azura Seraph™ Pty Ltd and its parent holding structures.
                      </p>
                    </div>

                    {/* SECTION 2 */}
                    <div className="space-y-2.5">
                      <h2 className="text-xs font-bold uppercase tracking-wider text-black border-b border-gray-300 pb-1">
                        2. EXECUTIVE DESIGNATIONS &amp; LANES OF RESPONSIBILITY
                      </h2>
                      
                      <div className="space-y-1">
                        <h3 className="text-xs font-bold text-black">
                          2.1 Founder &amp; Chief Executive Officer (CEO) — Raksha Singh:
                        </h3>
                        <ul className="list-disc pl-5 space-y-0.5 text-gray-800 text-xs leading-relaxed">
                          <li>Full-stack software architecture, product engineering, local-first trust engines, and AI integrations.</li>
                          <li>Global investor relations, venture capital raising, and primary Y Combinator institutional representation.</li>
                          <li>Strategic brand positioning, enterprise architecture, and primary platform governance.</li>
                        </ul>
                      </div>

                      <div className="space-y-1 pt-1">
                        <h3 className="text-xs font-bold text-black">
                          2.2 Co-Founder &amp; Strategic Chief Operating Officer (COO) — Thato D. Makgari:
                        </h3>
                        <ul className="list-disc pl-5 space-y-0.5 text-gray-800 text-xs leading-relaxed">
                          <li>Operational execution, institutional procurement, and enterprise pipeline management.</li>
                          <li>Corporate Enterprise &amp; Supplier Development (ESD) fund partnerships, banking cohort acquisition, and government compliance contracts.</li>
                          <li>South African subsidiary operational leadership, margin governance, and supply chain intelligence frameworks.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* RUNNING FOOTER - SHEET 1 */}
                  <div className="pt-3 mt-4 border-t border-gray-400 flex items-center justify-between text-[10px] text-gray-600">
                    <div>Azura Seraph™ (Pty) Ltd • CIPC Reg: 2024/560516/07</div>
                    <div className="font-bold text-gray-900 uppercase tracking-widest text-[10px]">Page 1 of 3</div>
                    <div className="text-right">Initials: RS [ _____ ] &nbsp; TM [ _____ ]</div>
                  </div>
                </div>
              </div>

              {/* ========================================================================= */}
              {/* SHEET 2 OF 3: EQUITY, VESTING, STIPEND, & US CBP ENTRY COMPLIANCE        */}
              {/* ========================================================================= */}
              <div>
                <div className="flex items-center justify-between px-2 pb-2 text-xs font-mono text-gray-400 no-print">
                  <span className="flex items-center gap-1.5 text-amber-400 font-bold">
                    <FileText size={14} />
                    PAGE 2 OF 3 • EQUITY ARCHITECTURE, YC STIPEND &amp; VISA FRAMEWORK
                  </span>
                  <span className="text-[11px] text-gray-500">A4 Standard Format (794×1123px)</span>
                </div>

                <div 
                  className="agreement-sheet bg-white text-slate-900 mx-auto shadow-md border border-gray-300 p-8 sm:p-12 flex flex-col justify-between"
                  style={{ 
                    fontFamily: 'Charter, Cambria, "Times New Roman", Times, Georgia, serif',
                    width: '100%',
                    maxWidth: '794px',
                    minHeight: '1123px',
                    boxSizing: 'border-box'
                  }}
                >
                  <div className="space-y-4">
                    {/* RUNNING HEADER */}
                    <div className="border-b border-gray-400 pb-1.5 flex items-center justify-between text-[10.5px] text-gray-700 uppercase tracking-wider">
                      <div className="font-bold text-black">AZURA SERAPH™ (PTY) LTD</div>
                      <div className="text-gray-600">EXECUTIVE FOUNDER GOVERNANCE AGREEMENT</div>
                      <div className="font-bold text-black">PAGE 2 OF 3</div>
                    </div>

                    {/* SECTION 3 */}
                    <div className="space-y-2">
                      <h2 className="text-xs font-bold uppercase tracking-wider text-black border-b border-gray-300 pb-1">
                        3. EQUITY ARCHITECTURE &amp; VESTING MECHANICS
                      </h2>

                      <div className="space-y-1">
                        <h3 className="text-xs font-bold text-black">
                          3.1 Equity Split Allocation:
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div className="p-2.5 border border-gray-300 bg-[#FAFAFA] rounded-none">
                            <div className="font-bold text-gray-950 text-xs">
                              • Raksha Singh (Founder &amp; CEO): 70% Equity Stake
                            </div>
                            <div className="text-[11px] text-gray-700 mt-0.5 leading-relaxed">
                              Reflecting total historical IP creation, software architecture, infrastructure deployment, Google Cloud Partner tier status, and foundational platform execution.
                            </div>
                          </div>

                          <div className="p-2.5 border border-gray-300 bg-[#FAFAFA] rounded-none">
                            <div className="font-bold text-gray-950 text-xs">
                              • Thato Donald Makgari (Co-Founder &amp; COO): 30% Equity Stake
                            </div>
                            <div className="text-[11px] text-gray-700 mt-0.5 leading-relaxed">
                              Reflecting operational leadership, institutional procurement, enterprise ESD cohort scaling, and long-term executive operational execution.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1 pt-1">
                        <h3 className="text-xs font-bold text-black">
                          3.2 Vesting Schedule (Standard Tier-1 Silicon Valley Governance):
                        </h3>
                        <ul className="list-disc pl-5 space-y-0.5 text-gray-800 text-xs leading-relaxed">
                          <li><strong>Total Vesting Horizon:</strong> 48 Months (4 Years).</li>
                          <li><strong>One-Year Cliff:</strong> 12-Month Cliff. Zero percent (0%) equity vests prior to Month 12. On exact Month 12, twenty-five percent (25%) of respective allocations vest automatically.</li>
                          <li><strong>Monthly Vesting:</strong> The remaining seventy-five percent (75%) vests in equal monthly increments (1/36th per month) across Months 13 through 48.</li>
                        </ul>
                      </div>
                    </div>

                    {/* SECTION 4 */}
                    <div className="space-y-2">
                      <h2 className="text-xs font-bold uppercase tracking-wider text-black border-b border-gray-300 pb-1">
                        4. PRE-SEED STIPEND &amp; Y COMBINATOR BATCH MECHANICS (SAN FRANCISCO)
                      </h2>
                      <p className="text-gray-800 text-xs leading-relaxed">
                        During the 3-month Y Combinator (YC) residency phase in San Francisco, California USA, the company operates under strict capital preservation parameters to maximize corporate runway:
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div className="p-2.5 border border-gray-300 bg-[#FAFAFA] space-y-1">
                          <h3 className="font-bold text-black text-xs">
                            4.1 De-Risking Founder Living Stipend:
                          </h3>
                          <ul className="list-disc pl-4 space-y-0.5 text-gray-800 text-[11.5px] leading-relaxed">
                            <li><strong>Monthly Stipend:</strong> $5,000 USD / month (~ ±R81,000 ZAR — forex dependent) paid directly to personal bank accounts.</li>
                            <li><strong>Purpose:</strong> Fully covers domestic living expenses, mortgages, school fees, and home obligations with zero personal financial risk.</li>
                          </ul>
                        </div>

                        <div className="p-2.5 border border-gray-300 bg-[#FAFAFA] space-y-1">
                          <h3 className="font-bold text-black text-xs">
                            4.2 Corporate Logistics (100% Company Paid):
                          </h3>
                          <ul className="list-disc pl-4 space-y-0.5 text-gray-800 text-[11px] leading-relaxed">
                            <li><strong>Accommodation:</strong> Shared executive 2-bedroom corporate housing in the Bay Area.</li>
                            <li><strong>Transit:</strong> Economy return airfare (SA ↔ SFO), US visa fees, and transit pool.</li>
                            <li><strong>Healthcare:</strong> Comprehensive international health &amp; travel insurance.</li>
                            <li><strong>Team Pool:</strong> Allocation for team work meals &amp; YC official events.</li>
                            <li><strong>Emergency Reserve:</strong> $2,000 USD corporate emergency buffer.</li>
                          </ul>
                        </div>
                      </div>

                      {/* VISA & TRAVEL ENTRY COMPLIANCE BOX */}
                      <div className="p-2.5 border border-gray-300 bg-[#FAFAFA] space-y-1 text-xs">
                        <div className="font-bold text-black text-xs border-b border-gray-300 pb-0.5 flex items-center justify-between">
                          <span>4.3 Non-Immigrant Business Entry Requirements (B1/B2 Visa)</span>
                          <span className="text-[10px] text-gray-600 uppercase font-sans">Official US CBP Guidelines</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] leading-relaxed">
                          <div>
                            <div className="font-bold text-black">Vaccination Compliance:</div>
                            <ul className="list-disc pl-4 space-y-0.5 text-gray-700">
                              <li><strong>COVID-19:</strong> The mandate requiring international travelers to show proof of COVID-19 vaccination was officially lifted on May 11, 2023. No proof required.</li>
                              <li><strong>Routine Vaccines:</strong> No routine vaccine verification at US border entry.</li>
                            </ul>
                          </div>

                          <div>
                            <div className="font-bold text-black">Core CBP Entry Documentation:</div>
                            <ul className="list-disc pl-4 space-y-0.5 text-gray-700">
                              <li><strong>Valid Passport:</strong> Valid 6+ months beyond intended period of stay.</li>
                              <li><strong>Valid B1/B2 Visa:</strong> Stamped in traveler passport.</li>
                              <li><strong>Intent of Return:</strong> Demonstrated home ties (South African entity, property).</li>
                              <li><strong>Business Purpose:</strong> Attending accelerator, pitches, meetings.</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RUNNING FOOTER - SHEET 2 */}
                  <div className="pt-3 mt-4 border-t border-gray-400 flex items-center justify-between text-[10px] text-gray-600">
                    <div>Azura Seraph™ (Pty) Ltd • CIPC Reg: 2024/560516/07</div>
                    <div className="font-bold text-gray-900 uppercase tracking-widest text-[10px]">Page 2 of 3</div>
                    <div className="text-right">Initials: RS [ _____ ] &nbsp; TM [ _____ ]</div>
                  </div>
                </div>
              </div>

              {/* ========================================================================= */}
              {/* SHEET 3 OF 3: POST-SEED ESCALATOR, WITNESS CLAUSE & DUAL SIGNATURES       */}
              {/* ========================================================================= */}
              <div>
                <div className="flex items-center justify-between px-2 pb-2 text-xs font-mono text-gray-400 no-print">
                  <span className="flex items-center gap-1.5 text-amber-400 font-bold">
                    <FileText size={14} />
                    PAGE 3 OF 3 • POST-SEED COMPENSATION &amp; DUAL LEGAL SIGNATURES
                  </span>
                  <span className="text-[11px] text-gray-500">A4 Standard Format (794×1123px)</span>
                </div>

                <div 
                  className="agreement-sheet bg-white text-slate-900 mx-auto shadow-md border border-gray-300 p-8 sm:p-12 flex flex-col justify-between"
                  style={{ 
                    fontFamily: 'Charter, Cambria, "Times New Roman", Times, Georgia, serif',
                    width: '100%',
                    maxWidth: '794px',
                    minHeight: '1123px',
                    boxSizing: 'border-box'
                  }}
                >
                  <div className="space-y-4">
                    {/* RUNNING HEADER */}
                    <div className="border-b border-gray-400 pb-1.5 flex items-center justify-between text-[10.5px] text-gray-700 uppercase tracking-wider">
                      <div className="font-bold text-black">AZURA SERAPH™ (PTY) LTD</div>
                      <div className="text-gray-600">EXECUTIVE FOUNDER GOVERNANCE AGREEMENT</div>
                      <div className="font-bold text-black">PAGE 3 OF 3</div>
                    </div>

                    {/* SECTION 5 */}
                    <div className="space-y-2">
                      <h2 className="text-xs font-bold uppercase tracking-wider text-black border-b border-gray-300 pb-1">
                        5. POST-SEED EXECUTIVE COMPENSATION ESCALATOR
                      </h2>
                      <p className="text-gray-900 text-xs leading-relaxed">
                        Upon the successful conclusion of the 3-month YC residency (Demo Day) and the closing of the institutional Seed Capital Round ($1.5M – $3.0M USD):
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-gray-800 text-xs leading-relaxed">
                        <li>Founder Stipends convert into full market-rate executive compensation packages (scaling up to $10,000 USD / month).</li>
                        <li>100% Full-time dedicated executive focus is formalized across US and South African operating entities.</li>
                      </ul>
                    </div>

                    {/* EXECUTION CLAUSE & LEGAL PLEDGE */}
                    <div className="pt-2 border-t border-gray-300 space-y-2">
                      <p className="text-xs italic text-gray-900 leading-relaxed">
                        IN WITNESS WHEREOF, the Parties hereto have signed and executed this Executive Partner Brief &amp; Founder Agreement on the date herein stated, affirming mutual executive duties, vesting adherence, and fiduciary commitments to Azura Seraph™ (Pty) Ltd and its parent corporate structures.
                      </p>
                      <div className="text-xs text-gray-800 pt-1">
                        SIGNED AT ____________________________ ON THIS _____ DAY OF ________________________ 2026.
                      </div>
                    </div>

                    {/* DUAL SIGNATURE BLOCKS */}
                    <div className="grid grid-cols-2 gap-8 pt-4">
                      {/* SIGNATURE 1: RAKSHA SINGH */}
                      <div className="space-y-2">
                        <div className="text-[10px] text-gray-600 uppercase font-sans font-bold tracking-wider">
                          FOR: AZURA SERAPH™ (PTY) LTD &amp; FOUNDER
                        </div>
                        <div className="h-12 border-b border-black flex items-end pb-1">
                          <span className="font-serif italic text-gray-400 text-xs">x ________________________________</span>
                        </div>
                        <div className="font-bold text-sm text-black uppercase">
                          Raksha Singh
                        </div>
                        <div className="text-xs text-gray-800">
                          Identity Number: <span className="font-mono font-semibold underline">{rakshaIdNumber || '_____________________________'}</span>
                        </div>
                        <div className="text-xs text-gray-600 leading-tight">
                          Founder &amp; Chief Executive Officer<br />
                          Azura Seraph™ (Pty) Ltd
                        </div>
                      </div>

                      {/* SIGNATURE 2: THATO DONALD MAKGARI */}
                      <div className="space-y-2">
                        <div className="text-[10px] text-gray-600 uppercase font-sans font-bold tracking-wider">
                          FOR: AZURA SERAPH™ (PTY) LTD &amp; CO-FOUNDER
                        </div>
                        <div className="h-12 border-b border-black flex items-end pb-1">
                          <span className="font-serif italic text-gray-400 text-xs">x ________________________________</span>
                        </div>
                        <div className="font-bold text-sm text-black uppercase">
                          Thato Donald Makgari
                        </div>
                        <div className="text-xs text-gray-800">
                          Identity Number: <span className="font-mono font-semibold underline">{thatoIdNumber || '___________________________'}</span>
                        </div>
                        <div className="text-xs text-gray-600 leading-tight">
                          Co-Founder &amp; Chief Operating Officer<br />
                          Azura Seraph™ (Pty) Ltd
                        </div>
                      </div>
                    </div>

                    {/* WITNESS EXECUTION ATTESTATION */}
                    <div className="pt-4 border-t border-gray-300">
                      <div className="text-[10px] text-gray-600 uppercase font-sans font-bold tracking-wider mb-2">
                        IN THE PRESENCE OF THE UNDERSIGNED WITNESSES:
                      </div>
                      <div className="grid grid-cols-2 gap-8 text-xs">
                        <div className="space-y-1">
                          <div><strong>WITNESS 1:</strong></div>
                          <div className="border-b border-gray-400 h-6"></div>
                          <div className="text-[11px] text-gray-600">Full Name: ___________________________</div>
                        </div>
                        <div className="space-y-1">
                          <div><strong>WITNESS 2:</strong></div>
                          <div className="border-b border-gray-400 h-6"></div>
                          <div className="text-[11px] text-gray-600">Full Name: ___________________________</div>
                        </div>
                      </div>
                    </div>

                    {/* CORPORATE REGULATORY REGISTRATION BADGE */}
                    <div className="p-2 border border-gray-300 bg-[#FAFAFA] flex flex-col sm:flex-row items-center justify-between gap-1 text-[10.5px] text-gray-700">
                      <div className="flex items-center gap-1.5 font-bold">
                        <ShieldCheck size={14} className="text-black shrink-0" />
                        <span>Registered Republic of South Africa CIPC: 2024/560516/07</span>
                      </div>
                      <div className="text-gray-600 font-sans text-[10px]">
                        Delaware C-Corp Pre-Flip Architecture
                      </div>
                    </div>
                  </div>

                  {/* RUNNING FOOTER - SHEET 3 */}
                  <div className="pt-3 mt-4 border-t border-gray-400 flex items-center justify-between text-[10px] text-gray-600">
                    <div>Azura Seraph™ (Pty) Ltd • CIPC Reg: 2024/560516/07</div>
                    <div className="font-bold text-gray-900 uppercase tracking-widest text-[10px]">Page 3 of 3 • Formal Execution</div>
                    <div className="text-right">Initials: RS [ _____ ] &nbsp; TM [ _____ ]</div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* 4. DOCUMENT DISPLAY - TAB 2: ADDENDUM */}
      {activeDoc === 'addendum' && (() => {
        const parsed = parseAddendum(addendumText, showFormalSignatures);
        
        return (
          <div className="space-y-4">
            {/* Quick ID Number Assistant (Synced with localStorage and Tab 1) */}
            <div className="p-4 bg-[#111416] border border-[#22282B] rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs no-print">
              <div className="flex items-center gap-2 text-gray-300">
                <Key size={16} className="text-amber-400" />
                <span className="font-semibold text-white">Identity Number Insertion Assistant:</span>
                <span className="text-[11px] text-gray-500 hidden md:inline">• Automatically formats Section 4 and Signature Block</span>
              </div>
              <div className="flex flex-wrap items-center gap-2.5">
                <input
                  type="text"
                  placeholder="Raksha Singh ID Number"
                  value={rakshaIdNumber}
                  onChange={(e) => setRakshaIdNumber(e.target.value)}
                  className="bg-[#181D1F] border border-[#2A2F31] rounded-xl px-3 py-1.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-400 text-xs"
                />
                <input
                  type="text"
                  placeholder="Thato D. Makgari ID Number"
                  value={thatoIdNumber}
                  onChange={(e) => setThatoIdNumber(e.target.value)}
                  className="bg-[#181D1F] border border-[#2A2F31] rounded-xl px-3 py-1.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-400 text-xs"
                />
              </div>
            </div>

            {/* LIVE DOCUMENT EDITOR & CONTROLS */}
            <div className="p-4 bg-[#111416] border border-[#22282B] rounded-2xl space-y-3 no-print">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                <div className="flex items-center gap-2 text-amber-400 font-bold text-xs uppercase">
                  <FileCheck size={16} />
                  <span>Strategic Addendum • Live Real-Time Synchronizer</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* DIRECT PDF DOWNLOAD IN TOOLBAR */}
                  <button
                    onClick={() => handleDownloadPdf('addendum')}
                    disabled={isGeneratingPdf}
                    className="px-3.5 py-1.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs rounded-lg transition flex items-center gap-1.5 cursor-pointer shadow-md disabled:opacity-50"
                    title="Download Strategic Addendum PDF (A4 Zero-Cut Engine)"
                  >
                    {generatingDoc === 'addendum' ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    <span>{generatingDoc === 'addendum' ? 'Rendering PDF...' : 'Download Addendum PDF'}</span>
                  </button>

                  <button
                    onClick={handleRemoveDuplicateSignature}
                    className="px-2.5 py-1.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-xs font-semibold rounded-lg border border-amber-500/40 transition flex items-center gap-1.5 cursor-pointer"
                    title="Strip trailing duplicate signatures from text"
                  >
                    <Scissors size={13} />
                    <span>Strip Duplicate Signatures</span>
                  </button>
                  <button
                    onClick={() => {
                      handleSaveAddendum(STRATEGIC_ADDENDUM_RAW_TEXT);
                      setShowFormalSignatures(true);
                      if (showToast) showToast('Restored official Addendum copy and signature block', 'info');
                    }}
                    className="px-2.5 py-1.5 bg-[#1A2022] hover:bg-[#252C2F] text-gray-300 text-xs font-semibold rounded-lg border border-[#2E3639] transition flex items-center gap-1.5 cursor-pointer"
                    title="Reset to official verbatim copy"
                  >
                    <RotateCcw size={13} />
                    <span>Reset Default Copy</span>
                  </button>
                </div>
              </div>

              {/* TOGGLES & CONFIGURATION BAR */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-1 border-t border-[#1F2427] text-xs">
                <div className="flex flex-wrap items-center gap-4">
                  {/* SIGNATURE BLOCK TOGGLE */}
                  <label className="flex items-center gap-2 text-gray-200 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={showFormalSignatures}
                      onChange={(e) => setShowFormalSignatures(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-600 text-amber-500 focus:ring-amber-500 bg-[#181D1F] cursor-pointer"
                    />
                    <span className="font-medium">Render Formal Dual-Signature Card at Bottom</span>
                  </label>

                  {/* VIEW MODE TOGGLE */}
                  <div className="flex items-center gap-1 bg-[#181D1F] p-1 rounded-lg border border-[#272E31]">
                    <button
                      onClick={() => setAddendumViewMode('formatted')}
                      className={`px-2.5 py-0.5 rounded text-[11px] font-semibold transition cursor-pointer ${
                        addendumViewMode === 'formatted'
                          ? 'bg-amber-500 text-black shadow-sm'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Legal Styled A4
                    </button>
                    <button
                      onClick={() => setAddendumViewMode('verbatim')}
                      className={`px-2.5 py-0.5 rounded text-[11px] font-semibold transition cursor-pointer ${
                        addendumViewMode === 'verbatim'
                          ? 'bg-amber-500 text-black shadow-sm'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Verbatim Plain Text
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-emerald-400 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    Live Synced with A4 Canvas Below
                  </span>
                  <button
                    onClick={() => setShowAddendumEditor(!showAddendumEditor)}
                    className="text-gray-400 hover:text-amber-400 text-xs underline cursor-pointer ml-1"
                  >
                    {showAddendumEditor ? 'Collapse Editor' : 'Expand Editor'}
                  </button>
                </div>
              </div>

              {/* LIVE TEXTAREA EDITOR */}
              {showAddendumEditor && (
                <div className="space-y-1.5 pt-1">
                  <div className="text-[11px] text-gray-400 flex items-center justify-between">
                    <span>Edit document copy in real time. Any changes, additions, or signature deletions update the live preview below instantly:</span>
                    <span className="font-mono text-[10px] text-gray-500">{addendumText.length} characters</span>
                  </div>
                  <textarea
                    rows={8}
                    value={addendumText}
                    onChange={(e) => handleSaveAddendum(e.target.value)}
                    placeholder="Paste or edit the verbatim text of the Strategic Addendum here..."
                    className="w-full bg-[#15191B] border border-[#2A3135] rounded-xl p-3.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-400 font-mono leading-relaxed transition"
                  />
                </div>
              )}
            </div>

            {/* LIVE ADDENDUM PDF PREVIEW CANVAS */}
            <div id="printable-addendum-root" className="bg-[#121516] border border-[#242A2D] rounded-2xl p-4 sm:p-8 shadow-lg overflow-x-auto">
              {/* Addendum Canvas Header Bar (Mirroring Founder Agreement) */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between px-2 pb-3 text-xs font-mono text-gray-400 gap-2 no-print border-b border-[#2A2F31] mb-6">
                <span className="flex items-center gap-2 text-amber-400 font-bold">
                  <FileCheck size={16} />
                  PAGE 1 OF 1 • STRATEGIC ADDENDUM: CORPORATE STRUCTURE &amp; GOVERNANCE
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-gray-500 font-mono">A4 Standard Format (794×1123px) • Zero Page Cuts</span>
                  <button
                    onClick={() => handleDownloadPdf('addendum')}
                    disabled={isGeneratingPdf}
                    className="px-3.5 py-1.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-md disabled:opacity-50"
                  >
                    {generatingDoc === 'addendum' ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    <span>{generatingDoc === 'addendum' ? 'Rendering PDF...' : 'Download Addendum PDF'}</span>
                  </button>
                  <button
                    onClick={handlePrint}
                    className="px-3 py-1.5 bg-[#1C2224] hover:bg-[#252C2F] text-gray-200 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer"
                    title="Print Strategic Addendum via Browser"
                  >
                    <Printer size={13} className="text-sky-400" />
                    <span>Print</span>
                  </button>
                </div>
              </div>

              <div id="addendum-pdf-canvas">
                <div 
                  className="agreement-sheet bg-white text-slate-900 mx-auto shadow-md border border-gray-300 p-8 sm:p-11 flex flex-col justify-between"
                  style={{ 
                    fontFamily: 'Charter, Cambria, "Times New Roman", Times, Georgia, serif',
                    width: '100%',
                    maxWidth: '794px',
                    minHeight: '1123px',
                    boxSizing: 'border-box'
                  }}
                >
                  {addendumViewMode === 'verbatim' ? (
                    /* VERBATIM PLAIN TEXT RENDERING */
                    <div className="space-y-4">
                      <div className="text-xs font-mono text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {addendumText}
                      </div>

                      {showFormalSignatures && (
                        <div className="grid grid-cols-2 gap-8 pt-4 border-t-2 border-black">
                          <div className="space-y-1.5">
                            <div className="text-[10px] text-gray-600 uppercase font-sans font-bold tracking-wider">
                              FOR: AZURA SERAPH™ (PTY) LTD &amp; FOUNDER
                            </div>
                            <div className="h-10 border-b border-black flex items-end pb-1">
                              <span className="font-serif italic text-gray-400 text-xs">x ________________________________</span>
                            </div>
                            <div className="font-bold text-sm text-black uppercase">
                              Raksha Singh
                            </div>
                            <div className="text-xs text-gray-800">
                              Identity Number: <span className="font-mono font-semibold underline">{rakshaIdNumber || '_____________________________'}</span>
                            </div>
                            <div className="text-xs text-gray-600 leading-tight">
                              Founder &amp; Chief Executive Officer<br />
                              Azura Seraph™ (Pty) Ltd
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <div className="text-[10px] text-gray-600 uppercase font-sans font-bold tracking-wider">
                              FOR: AZURA SERAPH™ (PTY) LTD &amp; CO-FOUNDER
                            </div>
                            <div className="h-10 border-b border-black flex items-end pb-1">
                              <span className="font-serif italic text-gray-400 text-xs">x ________________________________</span>
                            </div>
                            <div className="font-bold text-sm text-black uppercase">
                              Thato Donald Makgari
                            </div>
                            <div className="text-xs text-gray-800">
                              Identity Number: <span className="font-mono font-semibold underline">{thatoIdNumber || '___________________________'}</span>
                            </div>
                            <div className="text-xs text-gray-600 leading-tight">
                              Co-Founder &amp; Chief Operating Officer<br />
                              Azura Seraph™ (Pty) Ltd
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* DYNAMIC LEGAL STYLED RENDERING */
                    <div className="space-y-4">
                      {/* FORMAL LEGAL HEADER */}
                      <div className="text-center pb-3 border-b-2 border-black space-y-1">
                        <div className="text-[10px] tracking-[0.25em] text-gray-600 uppercase font-sans font-bold">
                          {parsed.headerMeta}
                        </div>
                        <h1 className="text-2xl font-bold tracking-tight text-black uppercase pt-0.5">
                          {parsed.headerCompany}
                        </h1>
                        <div className="text-xs text-gray-700">
                          {parsed.headerReg}
                        </div>
                        <div className="pt-1.5">
                          <span className="text-xs font-bold tracking-wider text-black uppercase border-y border-black py-0.5 px-3 inline-block">
                            {parsed.headerTitle}
                          </span>
                        </div>
                        <div className="text-[11px] font-bold text-gray-800 pt-0.5">
                          {parsed.headerDate}
                        </div>
                      </div>

                      {/* DYNAMICALLY PARSED SECTIONS */}
                      {parsed.sections.map((sec, sIdx) => {
                        const isAcknowledgment = sec.title.includes('ACKNOWLEDGMENT');
                        
                        return (
                          <div 
                            key={sIdx} 
                            className={`space-y-1.5 text-xs ${isAcknowledgment ? 'pt-1.5 border-t border-gray-300' : ''}`}
                          >
                            <h2 className="text-xs font-bold uppercase tracking-wider text-black border-b border-gray-300 pb-0.5">
                              {sec.title}
                            </h2>

                            <div className="space-y-1.5 pl-1.5 text-gray-900">
                              {sec.items.map((item, itemIdx) => {
                                if (item.type === 'sub') {
                                  return (
                                    <div key={itemIdx} className="leading-relaxed">
                                      <strong>{item.code} {item.label}</strong> {item.text}
                                    </div>
                                  );
                                }

                                if (item.type === 'bullet') {
                                  return (
                                    <div key={itemIdx} className="flex items-start gap-2 pl-3 text-gray-800 leading-relaxed">
                                      <span className="font-bold text-black select-none text-[10px] mt-0.5">•</span>
                                      <span>{item.text}</span>
                                    </div>
                                  );
                                }

                                // Check if paragraph mentions identity numbers to format
                                const rawText = item.text;
                                const hasRaksha = rawText.includes('Raksha Singh') && rawText.includes('Identity Number');
                                const hasThato = rawText.includes('Thato Donald Makgari') && rawText.includes('Identity Number');

                                if (hasRaksha || hasThato) {
                                  return (
                                    <p key={itemIdx} className="text-gray-900 leading-relaxed text-justify">
                                      This Addendum serves as an integral annexure to the primary Executive Partner Agreement executed between <strong>Raksha Singh</strong> — Identity Number: <span className="font-mono font-semibold underline">{rakshaIdNumber || '_______________________________'}</span> and <strong>Thato Donald Makgari</strong> — Identity Number: <span className="font-mono font-semibold underline">{thatoIdNumber || '___________________________'}</span>.
                                    </p>
                                  );
                                }

                                return (
                                  <p key={itemIdx} className="text-gray-900 leading-relaxed text-justify">
                                    {rawText}
                                  </p>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}

                      {/* DUAL SIGNATURE BLOCK (Controlled by state & clean formatting) */}
                      {showFormalSignatures && (
                        <div className="grid grid-cols-2 gap-8 pt-3 border-t-2 border-black">
                          <div className="space-y-1.5">
                            <div className="text-[10px] text-gray-600 uppercase font-sans font-bold tracking-wider">
                              FOR: AZURA SERAPH™ (PTY) LTD &amp; FOUNDER
                            </div>
                            <div className="h-10 border-b border-black flex items-end pb-1">
                              <span className="font-serif italic text-gray-400 text-xs">x ________________________________</span>
                            </div>
                            <div className="font-bold text-sm text-black uppercase">
                              Raksha Singh
                            </div>
                            <div className="text-xs text-gray-800">
                              Identity Number: <span className="font-mono font-semibold underline">{rakshaIdNumber || '_____________________________'}</span>
                            </div>
                            <div className="text-xs text-gray-600 leading-tight">
                              Founder &amp; Chief Executive Officer<br />
                              Azura Seraph™ (Pty) Ltd
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <div className="text-[10px] text-gray-600 uppercase font-sans font-bold tracking-wider">
                              FOR: AZURA SERAPH™ (PTY) LTD &amp; CO-FOUNDER
                            </div>
                            <div className="h-10 border-b border-black flex items-end pb-1">
                              <span className="font-serif italic text-gray-400 text-xs">x ________________________________</span>
                            </div>
                            <div className="font-bold text-sm text-black uppercase">
                              Thato Donald Makgari
                            </div>
                            <div className="text-xs text-gray-800">
                              Identity Number: <span className="font-mono font-semibold underline">{thatoIdNumber || '___________________________'}</span>
                            </div>
                            <div className="text-xs text-gray-600 leading-tight">
                              Co-Founder &amp; Chief Operating Officer<br />
                              Azura Seraph™ (Pty) Ltd
                            </div>
                          </div>
                        </div>
                      )}

                      {/* CORPORATE REGULATORY REGISTRATION BADGE */}
                      <div className="p-2 border border-gray-300 bg-[#FAFAFA] flex flex-col sm:flex-row items-center justify-between gap-1 text-[10.5px] text-gray-700">
                        <div className="flex items-center gap-1.5 font-bold">
                          <ShieldCheck size={14} className="text-black shrink-0" />
                          <span>Registered Republic of South Africa CIPC: 2024/560516/07</span>
                        </div>
                        <div className="text-gray-600 font-sans text-[10px]">
                          Delaware C-Corp Pre-Flip Architecture &amp; Governance Annexure
                        </div>
                      </div>
                    </div>
                  )}

                  {/* RUNNING FOOTER - ADDENDUM */}
                  <div className="pt-2 mt-3 border-t border-gray-400 flex items-center justify-between text-[10px] text-gray-600">
                    <div>Azura Seraph™ (Pty) Ltd • CIPC Reg: 2024/560516/07</div>
                    <div className="font-bold text-gray-900 uppercase tracking-widest text-[10px]">Page 1 of 1 • Strategic Addendum</div>
                    <div className="text-right">Initials: RS [ _____ ] &nbsp; TM [ _____ ]</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Bottom Reassurance & Action Bar (Matching Executive Partner Brief) */}
            <div className="p-4 bg-[#111416] border border-[#22282B] rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs no-print">
              <div className="space-y-0.5">
                <div className="font-bold text-white flex items-center gap-1.5">
                  <ShieldCheck size={15} className="text-emerald-400" />
                  <span>Strategic Addendum Ready for Execution &amp; Institutional Review</span>
                </div>
                <p className="text-[11px] text-gray-400">
                  Zero Page-Cut A4 print engine configured with identical vector resolution and font smoothing as the Executive Partner Brief.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  onClick={() => handleDownloadPdf('addendum')}
                  disabled={isGeneratingPdf}
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs rounded-xl shadow-md transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  title="Download Strategic Addendum PDF"
                >
                  {generatingDoc === 'addendum' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  <span>{generatingDoc === 'addendum' ? 'Rendering PDF...' : 'Download Strategic Addendum PDF'}</span>
                </button>
                <button
                  onClick={handlePrint}
                  className="px-3.5 py-2 bg-[#1C2224] hover:bg-[#252C2F] text-gray-200 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer"
                  title="Print Strategic Addendum via Browser"
                >
                  <Printer size={14} className="text-sky-400" />
                  <span>Print View</span>
                </button>
                <button
                  onClick={handleCopyText}
                  className="px-3 py-2 bg-[#1C2224] hover:bg-[#252C2F] text-gray-200 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer"
                  title="Copy Strategic Addendum Text"
                >
                  <Copy size={14} className="text-amber-400" />
                  <span>Copy Text</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
